/**
 * Partner-felvitel: a mező-mátrix ADAPTERE (61Y / partner-L1, 2. réteg).
 *
 * Az 1. réteg ([[partner-field-matrix]]) tiszta szabály-tábla: „ebben az ügytípusban
 * ez a mező kötelező-e". Ez a réteg köti a **konkrét űrlap-állapothoz**: a `formData`-ból
 * előállítja a mátrix kontextusát, és megmondja mezőnként, mit jelöljön a UI.
 *
 * Szándékosan komponens-mentes (nincs React-import) — így a szabályok a UI nélkül is
 * tesztelhetők, és a `PartnerCreatePage` (1000+ sor) érintetlen marad, amíg a
 * viselkedés nincs lefixálva.
 *
 * ── Kliens-ratify, ami ezt a réteget vezérli (2026-07-28 Zsuzsi mockup-ratify,
 *    Javo L0-válasz 2026-07-29) ────────────────────────────────────────────────
 *   • a BÉRLŐ (szerződő) MINDIG magánszemély; a cég **költségviselőként** jelenik meg
 *   • ezért a bérlés-oszlop feltétel nélkül kéri a személyi/születési/anyja-neve mezőket
 *   • ⚠️ DE cég-partnert NEM tiltunk: a meglévő cég-bérlős adat (Q-T160) migrációt
 *     igényelne. A ratify szerint L1-ben „ne tilts, csak ne kérj magánszemély-mezőket
 *     cégnél" → a `SUPPRESSED_FOR_COMPANY` mezők cégnél `not-applicable`-re esnek.
 */

import type {
  PartnerFieldKey,
  PartnerFormContext,
  RequirementCondition,
  TransactionIntent,
} from './partner-field-matrix';
import { PARTNER_FIELD_MATRIX, requirementReason } from './partner-field-matrix';
import type { PartnerType } from './types';

/** Az űrlap-állapotnak az a szelete, amiből a mátrix-kontextus levezethető. */
export interface PartnerFormSlice {
  type: PartnerType;
  taxNumber: string;
  discountPercent: string;
  discountRental: string;
  discountSales: string;
  discountService: string;
}

/**
 * Természetes személyhez kötött mezők — cég-partnernél értelmetlenek.
 * (Egy Kft.-nek nincs anyja neve és személyi igazolványa.)
 */
const SUPPRESSED_FOR_COMPANY: readonly PartnerFieldKey[] = [
  'idCard',
  'addressCard',
  'birthData',
  'mothersName',
];

/** Egy mező állapota az űrlapon — ezt fogyasztja a UI. */
export type FieldState =
  /** Kötelező; a `reason` mondja meg, miért (ügytípus vagy a teljesült feltétel). */
  | { status: 'required'; reason: 'ALWAYS' | RequirementCondition }
  /** Kitölthető, de nem várjuk el. */
  | { status: 'optional' }
  /** Ennél a partner-típusnál értelmetlen — a UI ne is mutassa. */
  | { status: 'not-applicable' };

/** Kitöltött-e egy kedvezmény-mező (üres és „0" nem számít kedvezménynek). */
function hasValue(raw: string): boolean {
  const trimmed = raw.trim();
  if (trimmed === '') return false;
  const num = Number(trimmed);
  return Number.isNaN(num) ? true : num !== 0;
}

/**
 * Az űrlap-állapot → mátrix-kontextus leképezés.
 *
 * ⚠️ A `hasCorporatePayer` **nem deriválható** a partner-űrlapból: a költségviselő a
 * tranzakció (bérlés/szerviz) oldalán dől el, nem a partner törzsadatában. Ezért a
 * hívó adja át — nem találjuk ki egy közeli mezőből.
 */
export function toMatrixContext(
  form: PartnerFormSlice,
  intent: TransactionIntent,
  options: { hasCorporatePayer?: boolean } = {}
): PartnerFormContext {
  const hasDiscount =
    hasValue(form.discountPercent) ||
    hasValue(form.discountRental) ||
    hasValue(form.discountSales) ||
    hasValue(form.discountService);

  return {
    intent,
    hasDiscount,
    // Céges (adószámos) számla: a cég-partner mindig számlát kap, illetve ha
    // magánszemélyhez adószám került, az kifejezetten céges számla-igény.
    wantsCompanyInvoice: form.type === 'company' || form.taxNumber.trim() !== '',
    ...(options.hasCorporatePayer === undefined
      ? {}
      : { hasCorporatePayer: options.hasCorporatePayer }),
  };
}

/** Egy mező állapota az adott űrlap-állapotban. */
export function fieldState(
  field: PartnerFieldKey,
  form: PartnerFormSlice,
  intent: TransactionIntent,
  options: { hasCorporatePayer?: boolean } = {}
): FieldState {
  if (form.type === 'company' && SUPPRESSED_FOR_COMPANY.includes(field)) {
    return { status: 'not-applicable' };
  }

  const reason = requirementReason(field, toMatrixContext(form, intent, options));
  return reason === null ? { status: 'optional' } : { status: 'required', reason };
}

/**
 * A soft-enforce összegzése: mely kötelező mezők hiányoznak.
 *
 * A mentést NEM blokkolja (kliens-ratify) — a UI ebből képzi a piros jelölést és a
 * figyelmeztetést. A `not-applicable` mezők sosem kerülnek bele.
 */
export function missingFields(
  form: PartnerFormSlice,
  intent: TransactionIntent,
  filled: Partial<Record<PartnerFieldKey, boolean>>,
  options: { hasCorporatePayer?: boolean } = {}
): PartnerFieldKey[] {
  return (Object.keys(PARTNER_FIELD_MATRIX) as PartnerFieldKey[]).filter(
    field => fieldState(field, form, intent, options).status === 'required' && !filled[field]
  );
}
